import { KeyboardEvent, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import { Combobox } from 'medere-ui-framework';
import LoadTable, { LoadTableColumn } from '../../../../../components/novarium/LoadTable';
import { AddRowButton, FieldError } from '../../../../../components/novarium/NvTableParts';
import { ComboDataDTO } from '../../../../../dto/ComboDataDTO';
import { NomenclatorDTO } from '../../../../../dto/NomenclatorDTO';
import useNotify from '../../../../../hooks/useNotify';
import { ApiErrorResponse, parseApiError } from '../../../../../services/apiError';
import { getBillingEntities, getProfessionalActivities, getProfessionals } from '../../../../../servicesHooks/Budget/useBudgetService';
import { formatMoney } from '../../../../../utils/money';
import {
  getAttentionModalities, getBuildingOptions, getPatientCoverages, registerOrder, valorizeOrder,
} from '../../../services/AgendaService';
import {
  CoverageOption, OrderAmounts, OrderDraft, OrderRow, OrderRowRequest, OrderValorization, RegisterOrderResponse,
  SelectOption,
} from '../../../types/billing.types';
import {
  errorMessageOf, FieldErrors, fieldErrorsOf, firstErrorWithPrefix, formatShortDate, fromApiDate, newRowKey,
  openBase64Pdf, toApiDate,
} from '../billingUtils';
import AmountsDialog from './AmountsDialog';
import AssociateOrdersDialog from './AssociateOrdersDialog';
import OtherConceptsDialog from './OtherConceptsDialog';
import ProfessionalsDialog from './ProfessionalsDialog';
import ValorizationInfoDialog from './ValorizationInfoDialog';

const GUARANTEE_OBSERVATION = 'Depósito en garantía';

interface Header {
  scheduleGroupId: number | null;
  modalityId: number | null;
  coverageId: number | null;
  billingEntity: SelectOption | null;
  /** Null hasta la primera valorización: el back toma los profesionales de la agenda. */
  professionalIds: number[] | null;
  guarantee: boolean;
  bonus: boolean;
  valorizationDate: Date;
}

interface OrderStepProps {
  appointmentId: number;
  patientName: string;
  /** "budget": "Presupuesto", el mismo paso solo para consultar los importes. */
  mode: 'order' | 'budget';
  /** « Volver de la pantalla: el paso decide si pregunta antes de salir. */
  exitRef?: MutableRefObject<(() => void) | null>;
  onRegistered?: (result: RegisterOrderResponse) => void;
  onCancel: () => void;
  onBudget?: () => void;
  onAssociated?: () => void;
}

/** Lo que la pantalla vuelve a mandar de cada fila: lo que la identifica, sus datos y los importes cambiados a mano. */
function toRequests(rows: OrderRow[], previous: OrderRowRequest[]): OrderRowRequest[] {
  const byKey = new Map(previous.map((row) => [row.rowKey, row]));
  return rows.map((row) => ({
    rowKey: row.rowKey,
    nomenclatorId: row.nomenclatorId,
    billingConceptId: row.billingConceptId,
    conceptDescription: row.conceptDescription,
    conceptFinancierAmount: row.conceptFinancierAmount,
    conceptPatientAmount: row.conceptPatientAmount,
    quantity: row.quantity,
    professionalIds: row.professionalIds,
    requestingDoctorId: row.requestingDoctor?.id ?? null,
    practiceDate: row.practiceDate,
    observations: row.observations,
    amounts: byKey.get(row.rowKey)?.amounts ?? null,
  }));
}

/** Como Vaadin: si cambia la cabecera se vuelve a valorizar y se pierden los importes cambiados a mano. */
function withoutAmounts(rows: OrderRowRequest[]): OrderRowRequest[] {
  return rows.map((row) => ({ ...row, amounts: null }));
}

/**
 * Paso "Orden" del cobro del turno, con los campos y las reglas del registro de órdenes de Vaadin
 * ({@code OrderRegistrationWindow} + {@code AppointmentValorizerPanel}). El back valoriza la orden completa cada vez
 * que cambia algo; la pantalla guarda solo lo que el usuario cargó.
 */
export default function OrderStep({
  appointmentId, patientName, mode, exitRef, onRegistered, onCancel, onBudget, onAssociated,
}: OrderStepProps) {
  const notify = useNotify();
  const isBudget = mode === 'budget';
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const [header, setHeader] = useState<Header>({
    scheduleGroupId: null, modalityId: null, coverageId: null, billingEntity: null, professionalIds: null,
    guarantee: false, bonus: false, valorizationDate: new Date(),
  });
  const [rows, setRows] = useState<OrderRowRequest[]>([]);
  const [valorization, setValorization] = useState<OrderValorization | null>(null);

  const [modalities, setModalities] = useState<SelectOption[]>([]);
  const [coverages, setCoverages] = useState<CoverageOption[]>([]);
  const [buildings, setBuildings] = useState<SelectOption[]>([]);
  const [buildingId, setBuildingId] = useState<number | null>(null);
  const [professionalOptions, setProfessionalOptions] = useState<SelectOption[]>([]);

  // Fila de alta y edición
  const [practice, setPractice] = useState<NomenclatorDTO | null>(null);
  const [quantity, setQuantity] = useState<number | null>(1);
  const [practiceDate, setPracticeDate] = useState<Date | null>(new Date());
  const [requestingDoctorId, setRequestingDoctorId] = useState<number | null>(null);
  const [practiceObservations, setPracticeObservations] = useState('');
  const [addErrors, setAddErrors] = useState<{ practice?: string; quantity?: string }>({});
  /** Fila que se edita en la fila de alta; null: la fila de alta agrega una práctica. */
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // Datos de la orden
  const [authorizationId, setAuthorizationId] = useState('');
  const [secondAuthorizationId, setSecondAuthorizationId] = useState('');
  const [auditCode, setAuditCode] = useState('');
  const [orderDate, setOrderDate] = useState<Date | null>(new Date());
  const [orderObservations, setOrderObservations] = useState('');

  // Diálogos
  const [amountsRow, setAmountsRow] = useState<OrderRow | null>(null);
  const [professionalsRow, setProfessionalsRow] = useState<OrderRow | null>(null);
  const [infoRow, setInfoRow] = useState<OrderRow | null>(null);
  const [conceptsOpen, setConceptsOpen] = useState(false);
  const [associateOpen, setAssociateOpen] = useState(false);
  const menuRef = useRef<Menu>(null);
  const [menuRow, setMenuRow] = useState<OrderRow | null>(null);
  const sequence = useRef(0);

  const toDraft = useCallback((h: Header, r: OrderRowRequest[] | null, loadGroupRows = false): OrderDraft => ({
    scheduleGroupId: h.scheduleGroupId,
    modalityId: h.modalityId,
    coverageId: h.bonus ? null : h.coverageId,
    bonus: h.bonus,
    billingEntityId: h.billingEntity?.id ?? null,
    professionalIds: h.professionalIds,
    guarantee: h.guarantee,
    valorizationDate: isBudget ? toApiDate(h.valorizationDate) : null,
    rows: r,
    loadGroupRows,
  }), [isBudget]);

  /**
   * Valoriza con la cabecera y las filas candidatas; si el back las acepta, pasan a ser las de la pantalla. Devuelve
   * el error para que quien la llamó lo muestre donde corresponde.
   */
  const revalue = useCallback(async (h: Header, r: OrderRowRequest[] | null, loadGroupRows = false): Promise<ApiErrorResponse | null> => {
    const current = ++sequence.current;
    setBusy(true);
    try {
      const data = await valorizeOrder(appointmentId, toDraft(h, r, loadGroupRows));
      if (current !== sequence.current) return null;
      setValorization(data);
      setHeader({
        ...h,
        scheduleGroupId: data.scheduleGroupId,
        modalityId: data.modalityId,
        coverageId: data.coverageId,
        billingEntity: data.billingEntity,
        professionalIds: data.professionals.map((p) => p.id),
      });
      setRows(toRequests(data.rows, r ?? []));
      setErrors((e) => {
        const next = { ...e };
        delete next.rows;
        return next;
      });
      return null;
    } catch (error: unknown) {
      return parseApiError(error);
    } finally {
      if (current === sequence.current) setBusy(false);
    }
  }, [appointmentId, toDraft]);

  /** Cambio hecho por el usuario: si el back no lo acepta, se avisa y la pantalla queda como estaba. */
  const change = useCallback(async (h: Header, r: OrderRowRequest[] | null, loadGroupRows = false) => {
    const apiError = await revalue(h, r, loadGroupRows);
    if (!apiError) {
      setDirty(true);
      return true;
    }
    const fieldErrors = fieldErrorsOf(apiError);
    if (Object.keys(fieldErrors).length > 0) setErrors((e) => ({ ...e, ...fieldErrors }));
    else notify.error(errorMessageOf(apiError, 'No se pudo valorizar la orden. Intentá de nuevo.'));
    return false;
  }, [revalue, notify]);

  // Carga inicial: catálogos y la orden con los defaults de la cabecera
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getAttentionModalities(),
      getPatientCoverages(appointmentId),
      isBudget ? Promise.resolve([] as SelectOption[]) : getBuildingOptions(appointmentId),
      getProfessionals({ filterValue: '', offSet: 0, pageSize: 99999 }).catch(() => []),
    ]).then(([modalityList, coverageList, buildingList, professionals]) => {
      if (cancelled) return;
      setModalities(modalityList);
      setCoverages(coverageList);
      setBuildings(buildingList);
      setBuildingId(buildingList.find((b) => b.isDefault)?.id ?? null);
      setProfessionalOptions(((professionals ?? []) as ComboDataDTO[])
        .filter((p) => p.id != null)
        .map((p) => ({ id: p.id as number, name: p.name ?? '' })));
    }).catch((error: unknown) => {
      parseApiError(error);
    });
    revalue({
      scheduleGroupId: null, modalityId: null, coverageId: null, billingEntity: null, professionalIds: null,
      guarantee: false, bonus: false, valorizationDate: new Date(),
    }, null).then((apiError) => {
      if (cancelled) return;
      if (apiError) {
        notify.error(errorMessageOf(apiError, 'No se pudo cargar la orden del turno. Intentá de nuevo.'));
        setLoadState('error');
      } else {
        setLoadState('ready');
      }
    });
    return () => { cancelled = true; };
    // Se carga una vez al abrir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const leave = useCallback(() => {
    if (!dirty || isBudget) {
      onCancel();
      return;
    }
    confirmDialog({
      header: '¿Salir sin guardar?',
      message: 'La orden que cargaste se va a perder.',
      className: 'nv-scope nv-confirm',
      footer: (options) => (
        <>
          <Button type="button" link label="Seguir editando" className="nv-push-left" onClick={options.reject} />
          <Button type="button" className="buttonMain2" outlined label="Salir sin guardar" onClick={options.accept} />
        </>
      ),
      accept: onCancel,
    });
  }, [dirty, isBudget, onCancel]);

  useEffect(() => {
    if (exitRef) exitRef.current = leave;
  }, [exitRef, leave]);

  const canViewCoverage = valorization?.canViewCoverageAmount ?? false;
  const rowByKey = (rowKey: string) => valorization?.rows.find((r) => r.rowKey === rowKey) ?? null;

  // --- Cabecera ---

  const changeHeader = (patch: Partial<Header>) => {
    setErrors({});
    void change({ ...header, ...patch }, withoutAmounts(rows));
  };

  const changeGuarantee = (guarantee: boolean) => {
    setErrors({});
    // Al volver a "Orden de prestación" se saca la observación que puso el depósito en garantía
    const nextRows = withoutAmounts(rows).map((row) => (!guarantee && row.observations === GUARANTEE_OBSERVATION
      ? { ...row, observations: null } : row));
    if (guarantee) {
      setAuthorizationId('');
      setSecondAuthorizationId('');
      setAuditCode('');
    }
    void change({ ...header, guarantee }, nextRows);
  };

  const changeGroup = (scheduleGroupId: number) => {
    setErrors({});
    // Como Vaadin: otro grupo de prestaciones vacía la grilla y carga sus prácticas
    void change({ ...header, scheduleGroupId }, [], true);
  };

  const toggleBonus = () => {
    setErrors({});
    void change({ ...header, bonus: !header.bonus, guarantee: false }, null);
  };

  // --- Filas ---

  const resetAddRow = () => {
    setPractice(null);
    setQuantity(1);
    setPracticeDate(new Date());
    setRequestingDoctorId(null);
    setPracticeObservations('');
    setAddErrors({});
    setEditingKey(null);
  };

  /** "Editar": los datos de la fila pasan a la fila de alta, que confirma o cancela los cambios. La práctica no cambia. */
  const startEdit = (rowKey: string) => {
    const row = rows.find((r) => r.rowKey === rowKey);
    if (!row) return;
    setEditingKey(rowKey);
    setPractice(null);
    setQuantity(row.quantity ?? 1);
    setPracticeDate(fromApiDate(row.practiceDate));
    setRequestingDoctorId(row.requestingDoctorId ?? null);
    setPracticeObservations(row.observations ?? '');
    setAddErrors({});
  };

  const submitAddRow = async () => {
    const validation: { practice?: string; quantity?: string } = {};
    if (!editingKey && (!practice?.nomenclator || practice.nomenclator <= 0)) validation.practice = 'Elegí la práctica';
    if (quantity == null || quantity < 1) validation.quantity = 'Ingresá una cantidad mayor a 0';
    setAddErrors(validation);
    if (Object.keys(validation).length > 0) return;
    const data = {
      quantity,
      practiceDate: practiceDate ? toApiDate(practiceDate) : null,
      requestingDoctorId,
      observations: practiceObservations.trim() || null,
    };
    const nextRows = editingKey
      // Como Vaadin: la cantidad nueva se vuelve a valorizar y se pierden los importes cambiados a mano
      ? rows.map((r) => (r.rowKey === editingKey ? { ...r, ...data, amounts: data.quantity === r.quantity ? r.amounts : null } : r))
      : [...rows, { rowKey: newRowKey('n'), nomenclatorId: practice!.nomenclator, ...data }];
    if (await change(header, nextRows)) resetAddRow();
  };

  const handleAddRowKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && editingKey) {
      event.preventDefault();
      resetAddRow();
    }
  };

  const addConcept = async (row: OrderRowRequest) => {
    if (await change(header, [...rows, row])) setConceptsOpen(false);
  };

  const removeRow = (rowKey: string) => {
    if (editingKey === rowKey) resetAddRow();
    void change(header, rows.filter((r) => r.rowKey !== rowKey));
  };

  const saveAmounts = async (rowKey: string, amounts: OrderAmounts, observations: string | null): Promise<string | null> => {
    const apiError = await revalue(header, rows.map((r) => (r.rowKey === rowKey ? { ...r, amounts, observations } : r)));
    if (!apiError) {
      setDirty(true);
      setAmountsRow(null);
      return null;
    }
    const fieldErrors = fieldErrorsOf(apiError);
    return Object.values(fieldErrors)[0] ?? errorMessageOf(apiError, 'No se pudieron cambiar los importes. Intentá de nuevo.');
  };

  const restoreAmounts = async (rowKey: string) => {
    if (await change(header, rows.map((r) => (r.rowKey === rowKey ? { ...r, amounts: null } : r)))) setAmountsRow(null);
  };

  const saveProfessionals = async (rowKey: string, professionalIds: number[]) => {
    if (await change(header, rows.map((r) => (r.rowKey === rowKey ? { ...r, professionalIds, amounts: null } : r)))) {
      setProfessionalsRow(null);
    }
  };

  // --- Registro ---

  async function register() {
    const validation: FieldErrors = {};
    if (!header.modalityId) validation.modalityId = 'Elegí la modalidad';
    if (!header.bonus && !header.coverageId) validation.coverageId = 'Elegí la obra social';
    if (!header.billingEntity) validation.billingEntityId = 'Elegí el emisor de la factura';
    if (rows.length === 0) validation.rows = 'Agregá al menos una práctica con importe';
    if (!orderDate) validation.orderDate = 'Completá la fecha de la orden';
    else if (toApiDate(orderDate) > toApiDate(new Date())) validation.orderDate = 'La fecha de la orden no puede ser posterior a hoy';
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    setSaving(true);
    try {
      const result = await registerOrder(appointmentId, {
        ...toDraft(header, rows),
        buildingId: buildings.length > 1 ? buildingId : null,
        authorizationId: header.guarantee ? undefined : authorizationId.trim() || undefined,
        secondAuthorizationId: header.guarantee ? undefined : secondAuthorizationId.trim() || undefined,
        auditCode: header.guarantee ? undefined : auditCode.trim() || undefined,
        orderObservations: orderObservations.trim() || undefined,
        orderDate: toApiDate(orderDate!),
      });
      if (result.authorizationPdf) openBase64Pdf(result.authorizationPdf);
      if (result.authorizationPdfFailed) notify.error('No se pudo generar el PDF de autorización');
      onRegistered?.(result);
    } catch (error: unknown) {
      const apiError = parseApiError(error);
      const fieldErrors = fieldErrorsOf(apiError);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors({ ...fieldErrors, rows: firstErrorWithPrefix(fieldErrors, 'rows') ?? '' });
      } else {
        notify.error(errorMessageOf(apiError, 'No se pudo registrar la orden. Intentá de nuevo.'));
      }
    } finally {
      setSaving(false);
    }
  }

  if (loadState === 'loading') {
    return <div className="nv-loading" role="status"><ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando…</div>;
  }
  if (loadState === 'error' || !valorization) {
    return (
      <>
        <p className="nv-note"><i className="pi pi-info-circle" aria-hidden /> No se pudo cargar la orden del turno.</p>
        <footer className="nv-page-foot">
          <hr className="nv-divider" />
          <div className="nv-actions-end">
            <Button type="button" className="buttonMain2" outlined label="Volver a la agenda" onClick={onCancel} />
          </div>
        </footer>
      </>
    );
  }

  const groups = valorization.scheduleGroups;
  const editingRow = editingKey ? rowByKey(editingKey) : null;
  // El médico de la fila que se edita puede no estar entre los profesionales del sitio (ej.: un médico externo)
  const editingDoctor = editingRow?.requestingDoctor;
  const doctorOptions = editingDoctor && !professionalOptions.some((p) => p.id === editingDoctor.id)
    ? [...professionalOptions, editingDoctor] : professionalOptions;
  const bonusUnavailable = !header.bonus && !valorization.bonusAvailable;
  const menuItems: MenuItem[] = menuRow ? [
    { label: 'Editar', icon: 'pi pi-pencil', command: () => startEdit(menuRow.rowKey) },
    {
      label: 'Modificar importes',
      // TODO(design-system): no hay ícono fijo para "Modificar importes" (el lápiz es de "Editar")
      icon: 'pi pi-dollar',
      // El back marca editable toda fila valorizada: sin modalidad, obra social o emisor todavía no hay importes
      disabled: !menuRow.editable,
      command: () => setAmountsRow(menuRow),
    },
    // TODO(design-system): no hay ícono fijo para "Cambiar profesionales"
    ...(menuRow.nomenclatorId != null ? [{ label: 'Cambiar profesionales', icon: 'pi pi-users', command: () => setProfessionalsRow(menuRow) }] : []),
    ...(menuRow.info ? [{ label: 'Ver valorización', icon: 'pi pi-eye', command: () => setInfoRow(menuRow) }] : []),
    { label: 'Quitar', icon: 'pi pi-trash', command: () => removeRow(menuRow.rowKey) },
  ] : [];

  const columns: LoadTableColumn<OrderRowRequest>[] = [
    {
      key: 'code',
      header: 'Código',
      align: 'center',
      className: 'nv-col-qty',
      body: (row) => rowByKey(row.rowKey)?.code || '—',
    },
    {
      key: 'description',
      header: 'Prestación',
      className: 'nv-col-item',
      addCell: editingRow ? (
        <span className="nv-truncate nv-tip" data-pr-tooltip={editingRow.description ?? ''}>{editingRow.description || '—'}</span>
      ) : (
        <div className="nv-cell-field">
          <Combobox
            inputId="order-add-practice"
            getItems={getProfessionalActivities}
            params={{ healthEntity: -1, orderByNomenclatorType: true }}
            optionLabel="codeanddescription"
            placeholder="Buscar práctica por código o descripción"
            minPanelWidth={600}
            maxPanelWidth={800}
            value={practice?.codeanddescription ?? ''}
            setValue={(value: NomenclatorDTO | null) => { setPractice(value ?? null); setAddErrors((e) => ({ ...e, practice: undefined })); }}
            error={Boolean(addErrors.practice)}
          />
          <FieldError id="order-add-practice-error" message={addErrors.practice} />
        </div>
      ),
      body: (row) => {
        const data = rowByKey(row.rowKey);
        const names = data?.professionals.map((p) => p.name).join(', ');
        return (
          <span className="nv-truncate nv-tip" data-pr-tooltip={names ? `${data?.description} (${names})` : data?.description ?? ''}>
            {data?.description || '—'}
          </span>
        );
      },
    },
    {
      key: 'quantity',
      header: 'Cantidad',
      align: 'center',
      className: 'nv-col-qty',
      addCell: (
        <div className="nv-cell-field" onKeyDown={handleAddRowKeyDown}>
          <InputNumber
            inputId="order-add-quantity"
            value={quantity}
            onValueChange={(e) => { setQuantity(e.value ?? null); setAddErrors((c) => ({ ...c, quantity: undefined })); }}
            min={1}
            max={99999}
            maxFractionDigits={0}
            className={addErrors.quantity ? 'nv-in-cell p-invalid' : 'nv-in-cell'}
            aria-label="Cantidad"
          />
          <FieldError id="order-add-quantity-error" message={addErrors.quantity} />
        </div>
      ),
      body: (row) => row.quantity ?? 1,
    },
    {
      key: 'authorization',
      header: 'Autorización',
      className: 'nv-col-status',
      body: (row) => {
        const data = rowByKey(row.rowKey);
        if (!data) return '—';
        return (
          <span className="nv-row-8">
            {data.authorizationError
              ? <Tag className="nv-tag-err" value={data.authorization || 'Convenio no encontrado'} />
              : <span>{data.authorization || '—'}</span>}
            {data.modified && <Tag className="nv-tag-warn" value="Modificado" />}
          </span>
        );
      },
    },
    {
      key: 'patient',
      header: 'Importe paciente',
      align: 'right',
      className: 'nv-col-money',
      body: (row) => {
        const data = rowByKey(row.rowKey);
        return (
          <span className="nv-stack-4">
            <span>{formatMoney(data?.patientAmount)}</span>
            {data?.patientLegend && <span className="nv-muted">{data.patientLegend}</span>}
          </span>
        );
      },
    },
    ...(canViewCoverage ? [{
      key: 'financier',
      header: 'Importe OS',
      align: 'right',
      className: 'nv-col-money',
      body: (row: OrderRowRequest) => {
        const data = rowByKey(row.rowKey);
        return (
          <span className="nv-stack-4">
            <span>{formatMoney(data?.financierAmount)}</span>
            {data?.financierLegend && <span className="nv-muted">{data.financierLegend}</span>}
          </span>
        );
      },
    } as LoadTableColumn<OrderRowRequest>] : []),
    {
      key: 'date',
      header: 'Fecha de pedido',
      align: 'center',
      className: 'nv-col-date-input',
      addCell: (
        <div className="nv-cell-field" onKeyDown={handleAddRowKeyDown}>
          <Calendar panelClassName="nv-scope" inputId="order-add-date" value={practiceDate} onChange={(e) => setPracticeDate((e.value as Date) ?? null)}
            dateFormat="dd/mm/yy" showIcon className="nv-in-cell" aria-label="Fecha de pedido" />
        </div>
      ),
      body: (row) => formatShortDate(row.practiceDate),
    },
    {
      key: 'doctor',
      header: 'Médico solicitante',
      addCell: (
        <div className="nv-cell-field" onKeyDown={handleAddRowKeyDown}>
          <Dropdown panelClassName="nv-scope"
            inputId="order-add-doctor"
            value={requestingDoctorId}
            options={doctorOptions}
            optionLabel="name"
            optionValue="id"
            filter
            showClear
            placeholder="Seleccionar"
            onChange={(e) => setRequestingDoctorId(e.value ?? null)}
            className="nv-in-cell"
            aria-label="Médico solicitante"
          />
        </div>
      ),
      body: (row) => {
        const name = rowByKey(row.rowKey)?.requestingDoctor?.name;
        return name ? <span className="nv-truncate nv-tip" data-pr-tooltip={name}>{name}</span> : '—';
      },
    },
    {
      key: 'observations',
      header: 'Observaciones',
      addCell: (
        <div className="nv-cell-field" onKeyDown={handleAddRowKeyDown}>
          <InputText id="order-add-observations" value={practiceObservations} maxLength={255} placeholder="Opcional"
            onChange={(e) => setPracticeObservations(e.target.value)} className="nv-in-cell" aria-label="Observaciones" />
        </div>
      ),
      body: (row) => (row.observations
        ? <span className="nv-truncate nv-tip" data-pr-tooltip={row.observations}>{row.observations}</span>
        : '—'),
    },
  ];

  const addAction = editingKey ? (
    <span className="nv-cell-actions">
      <Button type="button" text className="nv-icon nv-tip" icon="pi pi-check" aria-label="Confirmar cambios" data-pr-tooltip="Confirmar cambios"
        onClick={submitAddRow} loading={busy} />
      <Button type="button" text className="nv-icon nv-tip" icon="pi pi-times" aria-label="Cancelar cambios" data-pr-tooltip="Cancelar cambios"
        onClick={resetAddRow} disabled={busy} />
    </span>
  ) : (
    <AddRowButton label="Agregar práctica" onClick={submitAddRow} loading={busy} />
  );

  return (
    <>
      <Menu model={menuItems} popup ref={menuRef} className="nv-scope" />

      <section className="nv-section" aria-labelledby="order-header-title">
        <div className="nv-section-head">
          <h2 id="order-header-title" className="nv-section-title">Valorización</h2>
          {!isBudget && (
            <div className="nv-actions-end">
              <Button type="button" className="buttonMain3" icon="pi pi-link" label="Asociar" onClick={() => setAssociateOpen(true)} disabled={busy || saving} />
              <Button type="button" className="buttonMain3" label="Presupuesto" onClick={onBudget} disabled={busy || saving} />
            </div>
          )}
        </div>
        <div className="nv-form-row">
          {!isBudget && (
            <div className="nv-switch-field">
              <span
                className="nv-check-row nv-tip"
                data-pr-tooltip={bonusUnavailable ? 'No hay un plan para las órdenes bonificadas' : 'A cargo de la institución o del profesional'}
              >
                <InputSwitch inputId="order-bonus" checked={header.bonus} onChange={toggleBonus} disabled={busy || saving || bonusUnavailable} />
                <label htmlFor="order-bonus">Orden bonificada</label>
              </span>
            </div>
          )}
          {groups.length > 0 && (
            <div className="nv-field nv-w-m">
              <label htmlFor="order-group">Prestaciones asociadas</label>
              <Dropdown panelClassName="nv-scope" inputId="order-group" value={header.scheduleGroupId} options={groups} optionLabel="name" optionValue="id"
                placeholder="Seleccionar" onChange={(e) => changeGroup(e.value)} disabled={busy} />
            </div>
          )}
          {!isBudget && !header.bonus && (
            <div className="nv-field">
              <label id="order-type-label">Tipo de registro</label>
              <SelectButton
                aria-labelledby="order-type-label"
                value={header.guarantee ? 'guarantee' : 'order'}
                options={[{ label: 'Orden de prestación', value: 'order' }, { label: 'Depósito en garantía', value: 'guarantee' }]}
                onChange={(e) => { if (e.value) changeGuarantee(e.value === 'guarantee'); }}
                disabled={busy}
              />
            </div>
          )}
          {isBudget && (
            <div className="nv-field nv-w-s">
              <label htmlFor="order-date-valorization">Fecha</label>
              <Calendar panelClassName="nv-scope" inputId="order-date-valorization" value={header.valorizationDate} dateFormat="dd/mm/yy" showIcon
                onChange={(e) => { if (e.value) changeHeader({ valorizationDate: e.value as Date }); }} />
            </div>
          )}
          <div className="nv-field nv-w-s">
            <label htmlFor="order-modality">Modalidad <span className="nv-req">*</span></label>
            <Dropdown panelClassName="nv-scope" inputId="order-modality" value={header.modalityId} options={modalities} optionLabel="name" optionValue="id"
              placeholder="Seleccionar" onChange={(e) => changeHeader({ modalityId: e.value })} disabled={busy}
              className={errors.modalityId ? 'p-invalid' : undefined} />
            <FieldError id="order-modality-error" message={errors.modalityId} />
          </div>
          {header.bonus ? (
            <div className="nv-ro nv-w-m">
              <span className="nv-ro-label">Obra social y plan</span>
              <span className="nv-ro-value">{valorization.bonusPlan?.name ?? '—'}</span>
            </div>
          ) : (
            <div className="nv-field nv-w-l">
              <label htmlFor="order-coverage">Obra social <span className="nv-req">*</span></label>
              <Dropdown panelClassName="nv-scope" inputId="order-coverage" value={header.coverageId} options={coverages} optionLabel="name" optionValue="id"
                placeholder={coverages.length === 0 ? 'El paciente no tiene coberturas vigentes' : 'Seleccionar'}
                onChange={(e) => changeHeader({ coverageId: e.value })} disabled={busy || coverages.length === 0}
                className={errors.coverageId ? 'p-invalid' : undefined} />
              <FieldError id="order-coverage-error" message={errors.coverageId} />
            </div>
          )}
          {!isBudget && buildings.length > 1 && (
            <div className="nv-field nv-w-m">
              <label htmlFor="order-building">Edificio</label>
              <Dropdown panelClassName="nv-scope" inputId="order-building" value={buildingId} options={buildings} optionLabel="name" optionValue="id"
                placeholder="General" showClear onChange={(e) => { setBuildingId(e.value ?? null); setDirty(true); }} />
            </div>
          )}
          <div className="nv-field nv-w-m">
            <label htmlFor="order-billing-entity">Emisor de la factura <span className="nv-req">*</span></label>
            <Combobox
              inputId="order-billing-entity"
              getItems={getBillingEntities}
              optionLabel="name"
              placeholder="Seleccionar"
              value={header.billingEntity?.name ?? ''}
              setValue={(value: ComboDataDTO | null) => {
                if (!value?.id || value.id === header.billingEntity?.id) return;
                changeHeader({ billingEntity: { id: value.id, name: value.name ?? '' } });
              }}
              error={Boolean(errors.billingEntityId)}
            />
            <FieldError id="order-billing-entity-error" message={errors.billingEntityId} />
          </div>
          <div className="nv-field nv-w-l">
            <label htmlFor="order-professionals">Profesionales</label>
            <MultiSelect panelClassName="nv-scope"
              inputId="order-professionals"
              value={header.professionalIds ?? []}
              options={[...professionalOptions, ...valorization.professionals.filter((p) => !professionalOptions.some((o) => o.id === p.id))]}
              optionLabel="name"
              optionValue="id"
              display="chip"
              filter
              placeholder="Seleccionar"
              onChange={(e) => changeHeader({ professionalIds: e.value ?? [] })}
              disabled={busy}
            />
          </div>
        </div>
      </section>

      <section className="nv-section" aria-labelledby="order-rows-title">
        <div className="nv-section-head">
          <h2 id="order-rows-title" className="nv-section-title">Detalle de prestaciones</h2>
          <Button type="button" className="buttonMain2" outlined icon="pi pi-plus-circle" label="Agregar otros conceptos"
            onClick={() => setConceptsOpen(true)} disabled={busy} />
        </div>
        {!valorization.ready && (
          <p className="nv-note"><i className="pi pi-info-circle" aria-hidden /> Elegí la modalidad, la obra social y el emisor para valorizar las prácticas.</p>
        )}
        <LoadTable
          rows={rows}
          columns={columns}
          addAction={addAction}
          editingRowKey={editingKey}
          rowActions={(row) => (
            <Button
              type="button"
              text
              className="nv-icon nv-tip"
              icon="pi pi-ellipsis-v"
              aria-label="Acciones"
              data-pr-tooltip="Acciones"
              onClick={(event) => {
                setMenuRow(rowByKey(row.rowKey));
                menuRef.current?.toggle(event);
              }}
            />
          )}
          emptyMessage="Sin prácticas cargadas"
          footerLabel="Total"
          footerValues={{
            patient: formatMoney(valorization.patientTotal),
            ...(canViewCoverage ? { financier: formatMoney(valorization.financierTotal) } : {}),
          }}
        />
        <FieldError id="order-rows-error" message={errors.rows || undefined} />
      </section>

      {!isBudget && (
        <section className="nv-section" aria-labelledby="order-data-title">
          <h2 id="order-data-title" className="nv-section-title">Datos de la orden</h2>
          <div className="nv-form-row">
            {!header.guarantee && (
              <>
                <div className="nv-field nv-w-s">
                  <label htmlFor="order-auth">Código de autorización</label>
                  <InputText id="order-auth" value={authorizationId} maxLength={255} onChange={(e) => { setAuthorizationId(e.target.value); setDirty(true); }} />
                </div>
                <div className="nv-field nv-w-s">
                  <label htmlFor="order-auth-2">2do. código de autorización</label>
                  <InputText id="order-auth-2" value={secondAuthorizationId} maxLength={255} onChange={(e) => { setSecondAuthorizationId(e.target.value); setDirty(true); }} />
                </div>
                <div className="nv-field nv-w-s">
                  <label htmlFor="order-audit">Cód. auditoría</label>
                  <InputText id="order-audit" value={auditCode} maxLength={255} onChange={(e) => { setAuditCode(e.target.value); setDirty(true); }} />
                </div>
              </>
            )}
            <div className="nv-field nv-w-s">
              <label htmlFor="order-date">Fecha de la orden <span className="nv-req">*</span></label>
              <Calendar panelClassName="nv-scope" inputId="order-date" value={orderDate} maxDate={new Date()} dateFormat="dd/mm/yy" showIcon
                onChange={(e) => { setOrderDate((e.value as Date) ?? null); setErrors((c) => ({ ...c, orderDate: '' })); setDirty(true); }}
                className={errors.orderDate ? 'p-invalid' : undefined} />
              <FieldError id="order-date-error" message={errors.orderDate || undefined} />
            </div>
          </div>
          <div className="nv-field nv-w-l">
            <label htmlFor="order-observations">Observaciones</label>
            <InputTextarea id="order-observations" value={orderObservations} rows={2} maxLength={255} autoResize={false}
              onChange={(e) => { setOrderObservations(e.target.value); setDirty(true); }} />
          </div>
        </section>
      )}

      <footer className="nv-page-foot">
        <hr className="nv-divider" />
        <div className="nv-actions-end">
          {isBudget ? (
            <Button type="button" className="buttonMain" label="Cerrar" onClick={onCancel} />
          ) : (
            <>
              <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={leave} disabled={saving} />
              <Button type="button" className="buttonMain" label="Registrar orden" onClick={register} loading={saving} />
            </>
          )}
        </div>
      </footer>

      {amountsRow && (
        <AmountsDialog
          row={amountsRow}
          canViewCoverageAmount={canViewCoverage}
          onCancel={() => setAmountsRow(null)}
          onSave={(amounts, observations) => saveAmounts(amountsRow.rowKey, amounts, observations)}
          onRestore={() => restoreAmounts(amountsRow.rowKey)}
        />
      )}
      {professionalsRow && (
        <ProfessionalsDialog
          row={professionalsRow}
          options={professionalOptions}
          onCancel={() => setProfessionalsRow(null)}
          onSave={(ids) => saveProfessionals(professionalsRow.rowKey, ids)}
        />
      )}
      {infoRow?.info && <ValorizationInfoDialog info={infoRow.info} onClose={() => setInfoRow(null)} />}
      {conceptsOpen && (
        <OtherConceptsDialog canViewCoverageAmount={canViewCoverage} onCancel={() => setConceptsOpen(false)} onAdd={addConcept} />
      )}
      {associateOpen && (
        <AssociateOrdersDialog
          appointmentId={appointmentId}
          patientName={patientName}
          onCancel={() => setAssociateOpen(false)}
          onAssociated={() => { setAssociateOpen(false); onAssociated?.(); }}
        />
      )}
    </>
  );
}

